import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => { fetchOrders(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/orders`);
      setOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.patch(`${BACKEND}/api/orders/${orderId}/status`, { status });
      fetchOrders();
    } catch (e) { alert('Error updating status'); console.error(e); }
  };

  const statusColors = {
    pending: 'bg-[#FF9500]/20 text-[#FF9500]',
    processing: 'bg-[#007AFF]/20 text-[#007AFF]',
    shipped: 'bg-[#5856D6]/20 text-[#5856D6]',
    delivered: 'bg-[#34C759]/20 text-[#34C759]',
    cancelled: 'bg-[#FF3B30]/20 text-[#FF3B30]'
  };

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <h1 className="text-4xl font-light tracking-tighter text-white">Orders</h1>

      {loading ? <p className="text-[#A3A3A3]">Loading...</p> : orders.length === 0 ? (
        <p className="text-[#A3A3A3] text-center py-12">No orders yet</p>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="bg-[#141414] border border-white/10" data-testid={`order-card-${order.id}`}>
              <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                <div className="flex items-center space-x-6">
                  <div>
                    <p className="text-[#D4AF37] font-medium" data-testid={`order-number-${order.id}`}>{order.order_number}</p>
                    <p className="text-sm text-[#A3A3A3]">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-white">{order.customer_name}</p>
                    <p className="text-sm text-[#A3A3A3]">{order.customer_email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <p className="text-xl font-light text-white">&#8377;{order.total_amount.toLocaleString()}</p>
                  <span className={`px-3 py-1 text-xs uppercase tracking-wider ${statusColors[order.status] || ''}`} data-testid={`order-status-${order.id}`}>
                    {order.status}
                  </span>
                  <ChevronDown size={20} className={`text-[#A3A3A3] transition-transform ${expandedOrder === order.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="px-6 pb-6 border-t border-white/10 pt-4 space-y-4" data-testid={`order-detail-${order.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Phone</p>
                      <p className="text-white">{order.customer_phone}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Shipping Address</p>
                      <p className="text-white">{order.shipping_address}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-3">Items</p>
                    <div className="space-y-2">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm bg-black/30 p-3">
                          <span className="text-white">{item.product_name} x {item.quantity}</span>
                          <span className="text-[#D4AF37]">
                            &#8377;{(item.final_price * item.quantity).toLocaleString()}
                            {item.discount_value > 0 && <span className="text-[#A3A3A3] ml-2">discount applied</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(s => (
                        <button key={s} onClick={() => updateStatus(order.id, s)}
                          className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors border ${
                            order.status === s ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'border-white/20 text-[#A3A3A3] hover:text-white hover:border-white'
                          }`} data-testid={`order-status-btn-${s}-${order.id}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
